import { useThree } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import type {
	LegacyTimeOfDay,
	TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";
import {
	ACCENT_STRIP_THICKNESS_M,
	type AccentPool,
	type AccentStrip,
	getAccentLightingIntensity,
	planAccentLighting,
	type Vec3Tuple,
} from "./accentLightingLayout";

/**
 * Dusk accent lighting: warm LED lines under the lavar and along the
 * sockel, each with a soft pool of light on the surface below it, and a
 * backrest wash on the wall behind every lav that stands against one. See
 * `accentLightingLayout.ts` for what is placed where and why.
 *
 * Same render contract as the god rays (`LightShafts.tsx`): static meshes,
 * no `useFrame`, so on the demand frameloop they cost nothing once
 * painted; R3F repaints on the prop changes themselves. The pools are
 * additive, depth-tested (walls and benches occlude them) and never
 * write depth. Nothing is allocated before dusk:
 * at the default midday time of day this component renders nothing.
 */

/** Warm 2700 K LED. Matches the heater/interior glow family. */
const ACCENT_COLOR = new THREE.Color(0xffb86b);
/**
 * The strip itself is driven past 1.0 so the bloom pass on balanced+
 * picks it up as a lit line rather than a painted stripe.
 */
const STRIP_OVERDRIVE = 3.2;
/** Peak alpha of the pools at full dusk strength. */
const POOL_MAX_ALPHA = 0.6;

const POOL_VERTEX_SHADER = /* glsl */ `
varying vec2 vUv;
void main() {
	vUv = uv;
	gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

/**
 * Brightest at `uPeak` across the depth, falling off to zero at both
 * edges with a soft power curve; the last 8% of each end fades too, so
 * pools that meet at a corner never show a seam. The hash dither breaks
 * the additive banding AgX would otherwise quantise into rings.
 */
const POOL_FRAGMENT_SHADER = /* glsl */ `
uniform float uIntensity;
uniform float uPeak;
uniform vec3 uColor;
varying vec2 vUv;
float hash(vec2 p) {
	return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}
void main() {
	float v = vUv.y;
	float d = v < uPeak ? (uPeak - v) / max(uPeak, 1e-3) : (v - uPeak) / max(1.0 - uPeak, 1e-3);
	float across = pow(clamp(1.0 - d, 0.0, 1.0), 2.4);
	float ends = smoothstep(0.0, 0.08, vUv.x) * smoothstep(1.0, 0.92, vUv.x);
	float alpha = uIntensity * across * ends;
	alpha += (hash(gl_FragCoord.xy) - 0.5) * 0.01;
	gl_FragColor = vec4(uColor, clamp(alpha, 0.0, 1.0));
}
`;

const UP = new THREE.Vector3(0, 1, 0);

/**
 * Transform for a pool quad. PlaneGeometry lies in XY with +Y as its V
 * axis; this maps X → `along` and Y → `across`. A floor pool is laid flat
 * with its normal up; a wall wash (`across` = up) stands on the wall.
 */
function poolTransform(pool: AccentPool): {
	position: THREE.Vector3;
	quaternion: THREE.Quaternion;
	scale: THREE.Vector3;
} {
	const along = new THREE.Vector3(...pool.along);
	const across = new THREE.Vector3(...pool.across);
	const normal = new THREE.Vector3().crossVectors(along, across);
	// Keep a flat quad facing up whichever way `across` points.
	if (normal.dot(UP) < 0) along.negate();
	normal.crossVectors(along, across);
	const basis = new THREE.Matrix4().makeBasis(along, across, normal);
	return {
		position: new THREE.Vector3(...pool.center),
		quaternion: new THREE.Quaternion().setFromRotationMatrix(basis),
		scale: new THREE.Vector3(pool.length, pool.depth, 1),
	};
}

function stripRotationY(along: Vec3Tuple): number {
	return along[0] !== 0 ? 0 : Math.PI / 2;
}

interface SceneAccentLightingProps {
	interiorLighting?: boolean;
	/** Bumped after every dirty-tracked rebuild; the plan re-measures on it. */
	modelRevision: number;
	timeOfDay: TimeOfDayValue | LegacyTimeOfDay | number;
	/**
	 * Ground height under the building in the rig's frame, or `null` on a
	 * sloping plot (no flat ground to lay the sockel pools on).
	 */
	groundY: number | null;
}

export function SceneAccentLighting({
	modelRevision,
	timeOfDay,
	groundY,
	interiorLighting = true,
}: SceneAccentLightingProps) {
	const scene = useThree((state) => state.scene);
	const intensity = getAccentLightingIntensity(timeOfDay);
	const active = intensity > 0;

	const plan = useMemo(() => {
		if (!active) return null;
		// `modelRevision` is the rebuild signal, not a value we read.
		void modelRevision;
		const rig = scene?.getObjectByName("sauna-rig") ?? null;
		const plan = planAccentLighting(rig, rig, groundY);
		if (!interiorLighting) {
			plan.strips = plan.strips.filter(strip => strip.kind !== "bench");
			plan.pools = plan.pools.filter(pool => pool.kind !== "bench" && pool.kind !== "wall");
		}
		return plan;
	}, [active, scene, modelRevision, groundY, interiorLighting]);

	const hasAccents =
		plan !== null && plan.strips.length + plan.pools.length > 0;

	// One geometry per kind, shared by every mesh — scale carries the size.
	const stripGeometry = useMemo(
		() =>
			hasAccents
				? new THREE.BoxGeometry(
						1,
						ACCENT_STRIP_THICKNESS_M,
						ACCENT_STRIP_THICKNESS_M,
					)
				: null,
		[hasAccents],
	);
	const poolGeometry = useMemo(
		() => (hasAccents ? new THREE.PlaneGeometry(1, 1) : null),
		[hasAccents],
	);
	useEffect(
		() => () => {
			stripGeometry?.dispose();
			poolGeometry?.dispose();
		},
		[stripGeometry, poolGeometry],
	);

	const stripColor = useMemo(
		() =>
			ACCENT_COLOR.clone().multiplyScalar(0.35 + STRIP_OVERDRIVE * intensity),
		[intensity],
	);

	// Transforms and uniforms are memoised per plan/intensity: R3F must see
	// the SAME uniforms object across unrelated re-renders, or it swaps it
	// under a compiled program (see the 2026-07-15 sky-dome note).
	const pools = useMemo(
		() =>
			(plan?.pools ?? []).map((pool) => ({
				pool,
				transform: poolTransform(pool),
				uniforms: {
					uIntensity: { value: POOL_MAX_ALPHA * pool.strength * intensity },
					uPeak: { value: pool.peak },
					uColor: { value: ACCENT_COLOR },
				},
			})),
		[plan, intensity],
	);

	if (!plan || !hasAccents || !stripGeometry || !poolGeometry) return null;

	return (
		<group name="accentLighting/root">
			{plan.strips.map((strip: AccentStrip) => (
				<mesh
					key={`strip/${strip.id}`}
					name={`accentLighting/strip/${strip.id}`}
					geometry={stripGeometry}
					position={strip.center}
					rotation-y={stripRotationY(strip.along)}
					scale={[strip.length, 1, 1]}
					raycast={() => null}
					dispose={null}
				>
					<meshBasicMaterial color={stripColor} toneMapped />
				</mesh>
			))}
			{pools.map(({ pool, transform, uniforms }) => (
				<mesh
					key={`pool/${pool.id}`}
					name={`accentLighting/pool/${pool.id}`}
					geometry={poolGeometry}
					position={transform.position}
					quaternion={transform.quaternion}
					scale={transform.scale}
					renderOrder={2}
					raycast={() => null}
					dispose={null}
				>
					<shaderMaterial
						vertexShader={POOL_VERTEX_SHADER}
						fragmentShader={POOL_FRAGMENT_SHADER}
						uniforms={uniforms}
						transparent
						depthWrite={false}
						blending={THREE.AdditiveBlending}
						side={THREE.DoubleSide}
					/>
				</mesh>
			))}
		</group>
	);
}
